import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { AppRoute } from '@common/app/routes';
import { StringXSmall } from '@components/Typography/Typography';
import cn from 'classnames';

import { useGetCurrentLeagueInfoQuery } from '@/api/queries/league';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { milisecondsToTime } from '@/common/lib/timer';
import { LeagueTimer } from '@/components/ui/Timer/LeagueTimer';
import { useAppSelector } from '@/redux/store';

import { NavigationIcon } from './NavigationIcon';

import css from './styles.module.scss';

interface LeaguesNavigationItemProps {
  className?: string;
}

const LeaguesNavigationItem: FC<LeaguesNavigationItemProps> = ({
  className,
}) => {
  const activeLeague = useAppSelector((state) => state.leagues.list?.[0]);
  const { data: currentLeague } = useGetCurrentLeagueInfoQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { t } = useTranslation();

  const { event: { timeUntilNextOrStart = 0, active = false } = {} } =
    currentLeague || {};

  const setTimeLeague = activeLeague
    ? activeLeague.event.timeUntilNextOrStart
    : timeUntilNextOrStart;

  const setActiveLeague = activeLeague ? activeLeague.event.active : active;
  const leagueSeconds = useCountDownTimer(
    setTimeLeague < 0 ? 0 : setTimeLeague,
  );
  const leagueObjectTime = milisecondsToTime(leagueSeconds * 1000);

  const checkLeagueTimer: boolean = !!(leagueSeconds > 0);
  const checkLabel: boolean = !!(!setActiveLeague && leagueSeconds > 0);
  const label = 'Arena';

  return (
    <NavLink to={AppRoute.LEAGUES} className={className}>
      <div className={css.navItem}>
        <div className="navigationIcon relative">
          <NavigationIcon name="arena" active={setActiveLeague} />
        </div>
        <StringXSmall
          weight="medium"
          className={cn(
            checkLabel && 'rounded-[8px]',
            'navigationText relative text-nowrap whitespace-nowrap',
            !setActiveLeague &&
              'flex flex-col items-center !rounded-lg pt-[4px] pb-[6px] gap-[4px] h-[36px]',
          )}
        >
          {!setActiveLeague && (
            <span className="text-[10px] leading-none text-white/60 text-nowrap">
              {t('Next in')}
            </span>
          )}
          {checkLeagueTimer ? (
            <LeagueTimer value={leagueObjectTime} text={undefined} />
          ) : (
            t(label)
          )}
        </StringXSmall>
      </div>
    </NavLink>
  );
};

export { LeaguesNavigationItem };
